/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint react/prop-types: 0 */

import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import moment from "moment";
import { useEngineerAvailability } from "../../data/EngineerAvailability";
import { useWorkOrderItems } from "../../data/WorkItemHook";
import { useWorkOrders } from "../../data/WorkOrdersHook";
import { useProperty } from "../../data/PropertyHook";
import DayAvailability from "./DayAvailability";
import "./EngineerAvailability.css";

// Recommended = when an engineer is next on the same site
// Urgent = next 2 days if sla urgency is 48 hours or less even if there is no availability

export const getUTC4amDate = (date) =>
  new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 4));

const periodId = {
  AM: "PATS001",
  PM: "PATS002",
  "All day": "PATS003",
};

const slaUrgencyHours = 48;

const getProjectId = (projectId, workItemTemplateRef) => {
  if (
    workItemTemplateRef === "DomesticProject-PropertyInstallation" ||
    workItemTemplateRef === "DomesticCMV" ||
    workItemTemplateRef === "DomesticProject-PropertyOptimiseException" ||
    workItemTemplateRef === "DomesticProject-PropertyMeterException"
  ) {
    return projectId;
  }

  return "Response";
};

function EngineerAvailability({ updateAppointment, appointmentData }) {
  const engineerAvailabilityRepository = useEngineerAvailability();
  const workOrdersRepository = useWorkOrders();
  const propertyRepository = useProperty();
  const workItemsRepository = useWorkOrderItems();
  const currentWorkItem = workItemsRepository.getEntry();
  const [startDate, setStartDate] = useState(new Date());

  const selectedProperty = propertyRepository.selectedProperty();
  const territory = selectedProperty.territoryName;

  const workOrderEntry = workOrdersRepository.getEntry();

  const [isUrgent, setIsUrgent] = useState(false);

  const { siteId, projectId, workOrderworkitemcategoryname, slahours } = workOrderEntry;
  const { workitemtemplateref, workitemcategoryname } = currentWorkItem;

  const handleUrgentState = (
    workItemNameCatName,
    appointmentSlaHrs,
    slaUrgencyHrs
  ) => {
    setIsUrgent(
      workItemNameCatName === "Investigation" &&
        appointmentSlaHrs > 0 &&
        appointmentSlaHrs <= slaUrgencyHrs
    );
  };

  useEffect(() => {
    const f = async () => {
      await engineerAvailabilityRepository.updateEngineerAvailabilityV2({
        territory,
        siteId,
        projectId: getProjectId(projectId, workitemtemplateref),
        date: startDate.toISOString(),
        urgent: isUrgent,
        workItemTemplateRef: workitemtemplateref,
      });
    };

    f();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, isUrgent, siteId, projectId, workitemtemplateref, territory]);

  return (
    <EngineerAvailabilityInner
      data={engineerAvailabilityRepository.getEngineerAvailability()}
      updateAppointment={updateAppointment}
      appointmentData={{
        ...appointmentData,
        workItemCategory: workOrderEntry.workitemcategoryid,
      }}
      woSlaHours={slahours}
      startDate={startDate}
      setStartDate={setStartDate}
      handleUrgentState={handleUrgentState}
      isUrgent={isUrgent}
      workitemcategoryname={workitemcategoryname || workOrderworkitemcategoryname}
    />
  );
}

function EngineerAvailabilityInner({
  data,
  updateAppointment,
  appointmentData,
  woSlaHours,
  startDate,
  setStartDate,
  handleUrgentState,
  isUrgent,
  workitemcategoryname,
}) {
  const [filters, setFilters] = useState({
    period: "all",
    dayOfWeek: "all",
  });
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [showAllSlots, setShowAllSlots] = useState(false);
  const [hasRecommendedSlot, setHasRecommendedSlot] = useState(false);

  const {
    appointmentConfirmed,
    preferredAppointmentDate,
    preferredAppointmentSlotTitle,
    slahours,
  } = appointmentData;

  const appointmentIsNotAgreed = appointmentConfirmed === "WIA001";

  const suggestedStartBy = moment()
    .add(woSlaHours, "hours")
    .format("DD/MM/YYYY h:mm a");

  const handleSlotClick = (date, formattedDate, period) => {
    updateAppointment(
      getUTC4amDate(date),
      { id: periodId[period], title: period },
      true
    );
    setSelectedSlot(`${formattedDate}-${period}`);
  };

  const handleDateChange = (e) => {
    setStartDate(new Date(e.target.value));
    setSelectedSlot(null);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
  };

  const toggleShowAllSlots = () => {
    setShowAllSlots((prev) => !prev);
  };

  useEffect(() => {
    handleUrgentState(workitemcategoryname, slahours || 0, slaUrgencyHours);
    if (preferredAppointmentDate && preferredAppointmentSlotTitle) {
      setSelectedSlot(
        `${format(
          preferredAppointmentDate,
          "yyyy-MM-dd"
        )}-${preferredAppointmentSlotTitle}`
      );
    }

    setHasRecommendedSlot(
      data?.some((d) => d?.AM?.recommended || d?.PM?.recommended) || false
    );
  }, [
    data,
    preferredAppointmentDate,
    slahours,
    preferredAppointmentSlotTitle,
    handleUrgentState,
    workitemcategoryname,
  ]);

  useEffect(() => {
    if (appointmentIsNotAgreed) {
      updateAppointment(
        getUTC4amDate(new Date()),
        { id: periodId.AM, title: "AM" },
        true
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentIsNotAgreed]);

  useEffect(() => {
    if (hasRecommendedSlot) {
      setShowAllSlots(false);
    } else {
      setShowAllSlots(true);
    }
  }, [hasRecommendedSlot]);

  if (appointmentIsNotAgreed) {
    return (
      <div className="no-data-container">
        <div className="no-data-card">
          <p>Appointment not agreed</p>
        </div>
      </div>
    );
  }

  return (
    <div className="engineer-availability">
      <h3>Engineer Availability - {data && data[0]?.Territory}</h3>
      <div className="filters-and-selection">
        <div className="filters">
          <select
            data-testid="period"
            name="period"
            onChange={handleFilterChange}
            value={filters.period}
          >
            <option value="all">All Periods</option>
            <option value="am">AM Only</option>
            <option value="pm">PM Only</option>
          </select>
          <select
            data-testid="dayOfWeek"
            name="dayOfWeek"
            onChange={handleFilterChange}
            value={filters.dayOfWeek}
          >
            <option value="all">All Days</option>
            <option value="monday">Monday</option>
            <option value="tuesday">Tuesday</option>
            <option value="wednesday">Wednesday</option>
            <option value="thursday">Thursday</option>
            <option value="friday">Friday</option>
            <option value="saturday">Saturday</option>
            <option value="sunday">Sunday</option>
          </select>
          <div className="date-input-container">
            <label htmlFor="startDate">Start Date:</label>
            <input
              type="date"
              name="startDate"
              value={format(startDate, "yyyy-MM-dd")}
              onChange={handleDateChange}
              data-testid="startDate"
            />
          </div>
        </div>
        <div className="selected-slot-display">
          <div className="selected-slot-display-row">
            <label disabled>Suggested Start By:</label>
            <div>{suggestedStartBy}</div>
          </div>
          <div className="selected-slot-display-row">
            <div>Selected:</div>
            <div>
              {selectedSlot &&
                `${format(
                  selectedSlot.substring(0, 10),
                  "dd/MM/yyyy"
                )} ${selectedSlot.substring(11, 13)}`}
            </div>
          </div>
        </div>
      </div>
      <ul data-testid="engineer-availability-list">
        {data &&
          data.length > 0 &&
          data.map((dayData, i) => (
            <DayAvailability
              key={format(dayData.AvailabilityDate, "yyyy-MM-dd")}
              date={new Date(dayData.AvailabilityDate)}
              dayData={dayData}
              filters={filters}
              showAllSlots={showAllSlots}
              selectedSlot={selectedSlot}
              onSlotClick={handleSlotClick}
              isUrgentDay={isUrgent && (i === 0 || i === 1)} // we only want the first 2 days to be urgent
              appointmentData={appointmentData}
            />
          ))}
      </ul>

      {!data || data.length === 0 ? (
        <div className="no-data-container">
          <div className="no-data-card">
            <p>
              Create the Work Order as &#39;Not Agreed&#39; and record all
              occupier details as normal. E-mail scheduling, they will contact
              the occupier directly to agree an appointment.
            </p>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={toggleShowAllSlots}
          className="show-more-button"
        >
          {showAllSlots ? "Show Recommended Only" : "Show More Slots"}
        </button>
      )}
    </div>
  );
}

export default EngineerAvailability;
export { EngineerAvailabilityInner };
